import axios from "axios";
import Cookies from "js-cookie";

/**
 * API module for handling all server requests.
 *
 * @module api
 */
console.log("API URL:", process.env.REACT_APP_API_URL);
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true, // Ensure cookies are sent with requests
  timeout: 60000, // 60 seconds timeout
});

let csrfToken = null;

export const fetchCSRFToken = async () => {
  try {
    const response = await api.get("/csrf-token");
    csrfToken = response.data.csrfToken;
    return csrfToken;
  } catch (error) {
    console.error("Error fetching CSRF token:", error);
    throw error;
  }
};

api.interceptors.request.use(
  (config) => {
    const token = Cookies.get("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    const token = response.headers["authorization"];
    if (token) {
      Cookies.set("token", token.split(" ")[1], { sameSite: "Lax", path: "/" });
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.login = async (email, password, rememberMe) => {
  const response = await api.post("/users/login", {
    email,
    password,
    rememberMe,
  });
  if (response.data.user) {
    localStorage.setItem("user", JSON.stringify(response.data.user));
  }
  return response;
};

api.googleLogin = async (idToken) => {
  const response = await api.post("/auth/google", { idToken });
  if (response.data.user) {
    localStorage.setItem("user", JSON.stringify(response.data.user));
  }
  return response;
};

api.checkAuth = () => api.get("/users/check-auth");

api.logout = () => {
  localStorage.removeItem("token");
  return api.post("/users/logout");
};

api.getUserCards = () => api.get("/cards");
api.addCard = (cardData) => api.post("/cards", cardData);
api.getCardVouchers = (cardId) => api.get(`/vouchers/${cardId}`);
api.addVoucher = (cardId, voucherData) =>
  api.post(`/vouchers/${cardId}`, voucherData);
api.getAllCards = () => api.get("/admin/cards");
api.createCard = (cardData) => api.post("/admin/cards", cardData);
api.updateCard = (cardId, cardData) =>
  api.put(`/admin/cards/${cardId}`, cardData);

api.deleteCard = (cardId) => api.delete(`/admin/cards/${cardId}`);
api.getAllVouchers = () => api.get("/admin/vouchers");
api.createVoucher = (formData) =>
  api.post("/admin/vouchers", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
api.updateVoucher = (voucherId, formData) =>
  api.put(`/admin/vouchers/${voucherId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
api.deleteVoucher = (voucherId) => api.delete(`/admin/vouchers/${voucherId}`);
api.assignCardToUser = (cardId, userId) =>
  api.post("/admin/assign-card", { cardId, userId });
api.checkAdminStatus = () => api.get("/admin/check-status", {});

api.assignCardToUsers = (cardId, email, assignToAll) =>
  api.post("/admin/assign-card", { cardId, email, assignToAll });

api.getAllUsers = () => api.get("/admin/users");
api.getTotalUserCount = () => api.get("/users/count");
api.toggleOrganizerStatus = (userId) =>
  api.post(`/admin/users/${userId}/toggle-organizer`);
api.associateEventWithUser = (userId, eventId) =>
  api.post(`/admin/users/${userId}/associate-event`, { eventId });
api.getUserById = (userId) => api.get(`/admin/users/${userId}`);

api.sendVerificationCode = async (email) => {
  try {
    const response = await api.post("/users/send-verification-code", { email });
    return response;
  } catch (error) {
    console.error(
      "Error sending verification code:",
      error.response?.data || error.message
    );
    throw error;
  }
};

api.verifyCode = async (email, code) => {
  return await api.post("/users/verify-code", { email, code });
};

api.getUserPhone = () => api.get("/users/phone");

// Add these lines to the existing api.js file
api.getAllCompanies = () => api.get("/admin/companies");
// Public "places" list for the landing page — no auth required, unlike getAllCompanies above.
api.getPublicCompanies = (params = {}) => api.get("/companies", { params });
// Single company by id — PUBLIC /companies/:companyId route (field-limited).
api.getCompanyById = (companyId) => api.get(`/companies/${companyId}`);
// Admin-only full read of one place, for the editor — does not depend on the
// public endpoint. Returns the whole document.
api.getCompanyByIdAdmin = (companyId) => api.get(`/admin/companies/${companyId}/full`);
// Public /places/:city/:slug lookup. `slugOrId` also accepts a legacy Mongo
// _id so old pre-slug links keep resolving — see getCompanyBySlugOrId on the server.
api.getCompanyBySlug = (city, slugOrId) =>
  api.get(`/companies/by-slug/${encodeURIComponent(city)}/${encodeURIComponent(slugOrId)}`);
api.createCompany = (formData) =>
  api.post("/admin/companies", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

api.updateCompany = (companyId, formData) =>
  api.put(`/admin/companies/${companyId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  });
api.deleteCompany = (companyId) => api.delete(`/admin/companies/${companyId}`);

// Merchant relationship (isAdmin only) — contacts, follow-up state, and a
// paginated contact-log. All under /admin so the normal Bearer-token auth
// applies; stored in their own collections, never on the Company document.
api.getPlaceRelationship = (placeId) =>
  api.get(`/admin/places/${placeId}/relationship`);
api.savePlaceRelationship = (placeId, data) =>
  api.put(`/admin/places/${placeId}/relationship`, data);
api.getPlaceContactLogs = (placeId, params = {}) =>
  api.get(`/admin/places/${placeId}/contact-logs`, { params });
api.addPlaceContactLog = (placeId, data) =>
  api.post(`/admin/places/${placeId}/contact-logs`, data);
// Compact per-place summaries (primary contact name + next action date) for
// the admin Places list. isAdmin only; no emails/phones.
api.getPlaceRelationshipSummaries = () => api.get("/admin/place-relationships");

// Posts (admin)
api.getAllPostsAdmin = (params = {}) => api.get("/admin/posts", { params });
api.getPostByIdAdmin = (postId) => api.get(`/admin/posts/${postId}`);
api.createPost = (formData) =>
  api.post("/admin/posts", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
api.updatePost = (postId, formData) =>
  api.put(`/admin/posts/${postId}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    withCredentials: true,
  });
api.deletePost = (postId) => api.delete(`/admin/posts/${postId}`);

// Posts (public — not consumed anywhere yet, groundwork for the future Guides UI)
api.getPublicPosts = (params = {}) => api.get("/posts", { params });
api.getPostBySlug = (city, slug) =>
  api.get(`/posts/by-slug/${encodeURIComponent(city)}/${encodeURIComponent(slug)}`);

api.uploadImage = (formData) => {
  console.log("Token before update:", Cookies.get("token"));
  return api.post("/admin/upload-image", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const checkAuth = async () => {
  try {
    const response = await axios.get("/api/users/check-auth", {
      withCredentials: true,
    });
    if (response.status === 204) {
      return { data: { isAuthenticated: false } };
    }
    return response;
  } catch (error) {
    if (error.response && error.response.status === 401) {
      return { data: { isAuthenticated: false } };
    }
    throw error;
  }
};

// Add these new functions for event management
api.getAllEvents = async (params = {}) => {
  try {
    const response = await api.get("/events", { params });
    if (response.headers["content-type"].includes("application/json")) {
      return response;
    } else {
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Error in getAllEvents:", error);
    throw error;
  }
};
// 传入eventid 获取当前这个用户的这个event下面的所有用户
api.getEventUser = async (params = {}) => {
  try {
    const response = await api.get("/events/getEventUser", { params });
    if (response.headers["content-type"].includes("application/json")) {
      return response;
    } else {
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Error in getAllEvents:", error);
    throw error;
  }
};
// 传入eventid和userId 删除当前这个用户的这个event
api.delEventUser = async (params = {}) => {
  try {
    const response = await api.delete("/events/delEventUser", { params });
    if (response.headers["content-type"].includes("application/json")) {
      return response;
    } else {
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Error in getAllEvents:", error);
    throw error;
  }
};

// 传入用户isOrginsize和isobverser 用户id 以及对应的event 修改这个用户的类型和加入对应的事件
api.saveEventUser = async (params = {}) => {
  try {
    const response = await api.post("/events/saveEventUser", params);
    if (response.headers["content-type"].includes("application/json")) {
      return response;
    } else {
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Error in getAllEvents:", error);
    throw error;
  }
};

// Add these new functions for event management
api.getMyAllEvents = async (params = {}) => {
  try {
    const response = await api.get("/events/myEvents", { params });
    if (response.headers["content-type"].includes("application/json")) {
      return response;
    } else {
      throw new Error("Received non-JSON response");
    }
  } catch (error) {
    console.error("Error in getAllEvents:", error);
    throw error;
  }
};

// Update these functions to handle both ID and slug formats
api.getEventById = (identifier) => {
  // Check if it looks like a MongoDB ObjectId (24 hex chars)
  const isObjectId = /^[0-9a-fA-F]{24}$/.test(identifier);
  if (isObjectId) {
    return api.get(`/events/id/${identifier}`);
  } else {
    return api.get(`/events/slug/${identifier}`);
  }
};

// Keep this separate for explicit slug lookups
api.getEventBySlug = (slug) => api.get(`/events/slug/${slug}`);

api.createEvent = (eventData, config = {}) =>
  api.post("/admin/events", eventData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    ...config,
  });
api.updateEvent = (id, eventData, config = {}) =>
  api.put(`/admin/events/${id}`, eventData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    ...config,
  });
api.deleteEvent = (id) => api.delete(`/admin/events/${id}`);

api.checkExistingUser = (email, phoneNumber) =>
  api.post("/users/check-existing", { email, phoneNumber });

api.getLandingPageData = () => api.get("/landing");

api.getOrganizedEvents = () => api.get("/users/organized-events");

api.useTicket = (ticketId) => api.post(`/tickets/use-ticket/${ticketId}`);

// Update the verifyTicket function to include eventId
api.verifyTicket = (publicId, eventId) =>
  api.get(`/tickets/verify-ticket/${publicId}`, { params: { eventId } });

api.processPayment = async (paymentData) => {
  const response = await api.post("/payments/process", paymentData);
  return response.data;
};

api.createPaymentIntent = (paymentData) =>
  api.post("/payments/create-intent", paymentData);

api.createCheckoutSession = (paymentData) =>
  api.post("/payments/create-checkout-session", paymentData);

api.getUserTickets = () => api.get("/users/tickets");

api.getOrder = (sessionId) => api.get(`/orders/${sessionId}`);

api.requestPasswordReset = (email) =>
  api.post("/users/forgot-password", { email });
api.resetPassword = (token, newPassword) =>
  api.post("/users/reset-password", { token, newPassword });

api.createReferralCode = (eventId, code, influencer) => {
  return api.post("/referrals", { eventId, code, influencer });
};
api.trackReferralClick = (code) => {
  return api.get(`/referrals/track/${code}`);
};

// Add this new function
api.verifyTicket = (publicId, eventId) =>
  api.get(`/tickets/verify-ticket/${publicId}`, { params: { eventId } });

api.getUsersByEmail = (email) => api.get(`/users/email/${email}`);

api.updateUser = (userId, userData) => api.put(`/users/${userId}`, userData);

api.updateMe = (userData) => api.put(`/users/me`, userData);

api.inviteUser = (userData) => api.post(`/users/invite`, userData);

api.deleteReferralCode = (eventId, referralId) => {
  return api.delete(`/referrals/${eventId}/${referralId}`);
};

api.updateEventStatuses = () => api.post("/events/update-statuses");

api.getEventTicketCount = (eventId) =>
  api.get(`/events/${eventId}/ticket-count`);

api.getTicketCountsForEvents = (eventIds) =>
  api.get(`/events/ticket-counts`, { params: { eventIds: eventIds.join(",") } });

api.getEventFinancials = (eventId) => api.get(`/events/${eventId}/financials`);

api.getVoucherPreviews = async (limit = 4) => {
  try {
    const response = await api.get(`/vouchers/previews?limit=${limit}`);
    console.log("Voucher previews response:", response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching voucher previews:", error);
    return [];
  }
};

// params.withPlaces === true → server returns only cities that have at
// least one place (used by the public landing page dropdown). Omit for the
// full list (admin place/event assignment).
api.getAllCities = (params = {}) => api.get("/cities", { params });
api.createCity = (cityData) => api.post("/cities", cityData);

api.getReferralOrders = (eventId, referralId) =>
  api.get(`/referrals/${eventId}/${referralId}/orders`);

api.getMarketingEmails = () => api.get("/users/marketing-emails");

// Add these new endpoints
api.createFreeTickets = (eventId, ticketData) =>
  api.post(`/events/${eventId}/free-tickets`, ticketData);

api.getFreeTicketsList = (eventId) =>
  api.get(`/events/${eventId}/free-tickets`);

api.revokeFreeTicket = (ticketId, eventId) =>
  api.delete(`/events/${eventId}/free-tickets/${ticketId}/revoke`);

// Public "apply to become a partner" form on the landing page — no login required.
api.applyAsProspect = ({ name, businessName, email, phone, website }) =>
  api.post("/prospects/apply", { name, businessName, email, phone, website });

// Public newsletter signup — no login required, no account created.
api.subscribeNewsletter = ({ email, cities, source, consent }) =>
  api.post("/newsletter/subscribe", { email, cities, source, consent });

// Admin-only — read-only list of newsletter subscribers.
api.getNewsletterSubscribers = () => api.get("/newsletter/subscribers");

export default api;
